import {
    DASHBOARD_FILTERS_CHANGED_EVENT,
    DASHBOARD_SECTION_STATE_CHANGED_EVENT,
} from './events.js';

export function createDashboardFiltersController({ sectionsContainer, sectionMap, defaultOrder }) {
    const filtersDisclosure = document.querySelector('[data-dashboard-filters-disclosure]');
    const filtersToggle = document.querySelector('[data-dashboard-filters-toggle]');
    const filtersPanel = document.querySelector('[data-dashboard-filters-panel]');
    const sortSelect = document.querySelector('[data-dashboard-sort-select]');
    const allStatusFilterButton = document.querySelector('[data-dashboard-status-filter-all]');
    const statusFilterButtons = Array.from(document.querySelectorAll('[data-dashboard-status-filter]'));
    const tagFilterButtons = Array.from(document.querySelectorAll('[data-dashboard-tag-filter]'));
    const statusFilterButtonMap = createStatusFilterButtonMap(statusFilterButtons, sectionMap);

    const activeStatuses = new Set(defaultOrder);
    const activeTagIds = readTagIdsFromUrl(tagFilterButtons, new URL(window.location.href));

    function initialize() {
        bindFiltersDisclosureAnimation();
        bindStatusFilters();
        bindTagFilters();
        bindSortSelect();
        bindSectionStateUpdates();
        syncFilterQueryParams(activeTagIds, selectedSortValue());
        emitFiltersChanged(true, false);
    }

    function bindFiltersDisclosureAnimation() {
        if (
            !(filtersDisclosure instanceof HTMLDetailsElement)
            || !(filtersToggle instanceof HTMLElement)
            || !(filtersPanel instanceof HTMLElement)
        ) {
            return;
        }

        let activeAnimation = null;
        let isClosing = false;
        let isExpanding = false;
        const mobileMediaQuery = window.matchMedia(
            `(max-width: ${resolveCssLengthVariable('--bp-mobile-max', '768px')})`,
        );

        applyResponsiveDisclosureState(mobileMediaQuery.matches);
        mobileMediaQuery.addEventListener('change', (event) => {
            applyResponsiveDisclosureState(event.matches);
        });

        filtersToggle.addEventListener('click', (event) => {
            event.preventDefault();

            if (typeof filtersDisclosure.animate !== 'function' || prefersReducedMotion()) {
                filtersDisclosure.open = filtersDisclosure.open === false;
                syncFiltersToggleExpandedState(filtersDisclosure.open);
                return;
            }

            if (isClosing || filtersDisclosure.open === false) {
                openDisclosure();
                return;
            }

            if (isExpanding || filtersDisclosure.open) {
                closeDisclosure();
            }
        });

        function closeDisclosure() {
            isClosing = true;
            isExpanding = false;
            filtersDisclosure.classList.add('is-collapsing');

            const startHeight = `${filtersDisclosure.offsetHeight}px`;
            const endHeight = `${filtersToggle.offsetHeight}px`;

            if (activeAnimation instanceof Animation) {
                activeAnimation.cancel();
            }

            filtersDisclosure.style.overflow = 'hidden';
            filtersDisclosure.style.height = startHeight;
            activeAnimation = filtersDisclosure.animate({
                height: [startHeight, endHeight],
            }, {
                duration: 220,
                easing: 'cubic-bezier(0.4, 0, 0.2, 1)',
                fill: 'forwards',
            });

            activeAnimation.onfinish = () => {
                handleAnimationFinish(false);
            };
            activeAnimation.oncancel = handleAnimationCancel;
        }

        function openDisclosure() {
            isClosing = false;
            isExpanding = true;
            filtersDisclosure.classList.remove('is-collapsing');

            const startHeight = `${filtersDisclosure.offsetHeight}px`;
            filtersDisclosure.style.height = startHeight;
            filtersDisclosure.open = true;

            window.requestAnimationFrame(() => {
                const endHeight = `${filtersToggle.offsetHeight + filtersPanel.offsetHeight}px`;

                if (activeAnimation instanceof Animation) {
                    activeAnimation.cancel();
                }

                filtersDisclosure.style.overflow = 'hidden';
                activeAnimation = filtersDisclosure.animate({
                    height: [startHeight, endHeight],
                }, {
                    duration: 240,
                    easing: 'cubic-bezier(0.4, 0, 0.2, 1)',
                    fill: 'forwards',
                });

                activeAnimation.onfinish = () => {
                    handleAnimationFinish(true);
                };
                activeAnimation.oncancel = handleAnimationCancel;
            });
        }

        function handleAnimationFinish(shouldBeOpen) {
            filtersDisclosure.open = shouldBeOpen;
            filtersDisclosure.classList.remove('is-collapsing');
            filtersDisclosure.style.height = '';
            filtersDisclosure.style.overflow = '';
            syncFiltersToggleExpandedState(shouldBeOpen);
            activeAnimation = null;
            isClosing = false;
            isExpanding = false;
        }

        function handleAnimationCancel() {
            filtersDisclosure.classList.remove('is-collapsing');
            activeAnimation = null;
            isClosing = false;
            isExpanding = false;
        }

        function applyResponsiveDisclosureState(isMobileViewport) {
            if (isMobileViewport) {
                filtersDisclosure.open = false;
            } else {
                filtersDisclosure.open = true;
            }

            filtersDisclosure.classList.remove('is-collapsing');
            filtersDisclosure.style.height = '';
            filtersDisclosure.style.overflow = '';
            syncFiltersToggleExpandedState(filtersDisclosure.open);
        }

        function syncFiltersToggleExpandedState(isOpen) {
            filtersToggle.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
        }
    }

    function bindStatusFilters() {
        const allStatusValues = defaultOrder.filter((statusValue) => sectionMap.has(statusValue));

        if (allStatusFilterButton instanceof HTMLElement) {
            allStatusFilterButton.addEventListener('click', () => {
                activeStatuses.clear();
                allStatusValues.forEach((statusValue) => {
                    activeStatuses.add(statusValue);
                });

                syncStatusFilterStates(allStatusValues);
                emitFiltersChanged(false, false);
            });
        }

        statusFilterButtons.forEach((button) => {
            if (!(button instanceof HTMLElement)) {
                return;
            }

            const statusValue = String(button.dataset.status ?? '').trim();
            if (statusValue === '' || !sectionMap.has(statusValue)) {
                return;
            }

            button.addEventListener('click', () => {
                if (activeStatuses.has(statusValue)) {
                    activeStatuses.delete(statusValue);
                } else {
                    activeStatuses.add(statusValue);
                }

                syncStatusFilterStates(allStatusValues);
                emitFiltersChanged(false, false);
            });
        });

        syncStatusFilterStates(allStatusValues);
    }

    function syncStatusFilterStates(allStatusValues) {
        statusFilterButtons.forEach((button) => {
            if (!(button instanceof HTMLElement)) {
                return;
            }

            const statusValue = String(button.dataset.status ?? '').trim();
            if (statusValue === '' || !sectionMap.has(statusValue)) {
                return;
            }

            setFilterChipState(button, activeStatuses.has(statusValue));
        });

        if (!(allStatusFilterButton instanceof HTMLElement)) {
            return;
        }

        const areAllStatusesActive = allStatusValues.length > 0
            && allStatusValues.every((statusValue) => activeStatuses.has(statusValue));
        setFilterChipState(allStatusFilterButton, areAllStatusesActive);
    }

    function bindTagFilters() {
        tagFilterButtons.forEach((button) => {
            if (!(button instanceof HTMLElement)) {
                return;
            }

            const tagId = String(button.dataset.tagId ?? '').trim();
            if (tagId === '') {
                return;
            }

            setFilterChipState(button, activeTagIds.has(tagId));
            button.addEventListener('click', () => {
                if (activeTagIds.has(tagId)) {
                    activeTagIds.delete(tagId);
                } else {
                    activeTagIds.add(tagId);
                }

                setFilterChipState(button, activeTagIds.has(tagId));
                syncFilterQueryParams(activeTagIds, selectedSortValue());
                emitFiltersChanged(false, true);
            });
        });
    }

    function bindSortSelect() {
        if (!(sortSelect instanceof HTMLSelectElement)) {
            return;
        }

        sortSelect.addEventListener('change', () => {
            syncFilterQueryParams(activeTagIds, sortSelect.value);
            emitFiltersChanged(true, true);
        });
    }

    function bindSectionStateUpdates() {
        sectionsContainer.addEventListener(DASHBOARD_SECTION_STATE_CHANGED_EVENT, (event) => {
            const detail = event instanceof CustomEvent && isObject(event.detail)
                ? event.detail
                : null;
            if (detail === null) {
                return;
            }

            const statusValue = typeof detail.statusValue === 'string' ? detail.statusValue : '';
            const button = statusFilterButtonMap.get(statusValue);
            if (!(button instanceof HTMLElement)) {
                return;
            }

            if (detail.isLoading === true) {
                button.hidden = false;
                return;
            }

            button.hidden = detail.isEmpty === true;
        });
    }

    function emitFiltersChanged(forceReload, resetPages) {
        sectionsContainer.dispatchEvent(new CustomEvent(DASHBOARD_FILTERS_CHANGED_EVENT, {
            detail: {
                activeStatuses: Array.from(activeStatuses),
                activeTagIds: Array.from(activeTagIds),
                tagIdsKey: createTagIdsKey(activeTagIds),
                sortValue: selectedSortValue(),
                forceReload,
                resetPages,
            },
        }));
    }

    function selectedSortValue() {
        return sortSelect instanceof HTMLSelectElement && sortSelect.value.trim() !== ''
            ? sortSelect.value.trim()
            : 'playtime';
    }

    function restoreFromUrl(publicUrl) {
        if (!(publicUrl instanceof URL)) {
            return false;
        }

        const restoredTagIds = readTagIdsFromUrl(tagFilterButtons, publicUrl);
        const restoredSortValue = readSortFromUrl(sortSelect, publicUrl);
        const hasChanged = createTagIdsKey(restoredTagIds) !== createTagIdsKey(activeTagIds)
            || restoredSortValue !== selectedSortValue();

        activeTagIds.clear();
        restoredTagIds.forEach((tagId) => activeTagIds.add(tagId));
        tagFilterButtons.forEach((button) => {
            if (!(button instanceof HTMLElement)) {
                return;
            }

            const tagId = String(button.dataset.tagId ?? '').trim();
            setFilterChipState(button, tagId !== '' && activeTagIds.has(tagId));
        });
        if (sortSelect instanceof HTMLSelectElement) {
            sortSelect.value = restoredSortValue;
        }

        syncFilterQueryParams(activeTagIds, restoredSortValue);
        if (hasChanged) {
            emitFiltersChanged(true, false);
        }

        return hasChanged;
    }

    return {
        initialize,
        restoreFromUrl,
    };
}

function createStatusFilterButtonMap(buttons, sectionMap) {
    const map = new Map();

    buttons.forEach((button) => {
        if (!(button instanceof HTMLElement)) {
            return;
        }

        const statusValue = String(button.dataset.status ?? '').trim();
        if (statusValue === '' || !sectionMap.has(statusValue) || map.has(statusValue)) {
            return;
        }

        map.set(statusValue, button);
    });

    return map;
}

function setFilterChipState(button, isActive) {
    button.classList.toggle('is-active', isActive);
    button.classList.toggle('is-inactive', isActive === false);
    button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
}

function createTagIdsKey(tagIds) {
    return Array.from(tagIds)
        .sort((left, right) => left.localeCompare(right))
        .join('|');
}

function readTagIdsFromUrl(buttons, url) {
    const availableTagIds = new Set(buttons
        .filter((button) => button instanceof HTMLElement)
        .map((button) => String(button.dataset.tagId ?? '').trim())
        .filter((tagId) => tagId !== ''));
    const rawTagIds = url.searchParams.get('tag_ids') ?? '';

    return new Set(rawTagIds
        .split(',')
        .map((tagId) => tagId.trim())
        .filter((tagId) => availableTagIds.has(tagId)));
}

function readSortFromUrl(sortSelect, url) {
    const requestedSort = url.searchParams.get('sort') ?? 'playtime';
    if (!(sortSelect instanceof HTMLSelectElement)) {
        return 'playtime';
    }

    return Array.from(sortSelect.options ?? [])
        .some((option) => option?.value === requestedSort)
        ? requestedSort
        : 'playtime';
}

function syncFilterQueryParams(tagIds, sortValue) {
    const currentUrl = new URL(window.location.href);
    const normalizedTagIds = Array.from(tagIds).sort((left, right) => left.localeCompare(right));
    const normalizedSortValue = typeof sortValue === 'string' ? sortValue.trim() : '';

    if (normalizedTagIds.length === 0) {
        currentUrl.searchParams.delete('tag_ids');
    } else {
        currentUrl.searchParams.set('tag_ids', normalizedTagIds.join(','));
    }

    if (normalizedSortValue === '' || normalizedSortValue === 'playtime') {
        currentUrl.searchParams.delete('sort');
    } else {
        currentUrl.searchParams.set('sort', normalizedSortValue);
    }

    window.history.replaceState(
        window.history.state ?? null,
        '',
        `${currentUrl.pathname}${currentUrl.search}${currentUrl.hash}`,
    );
}

function isObject(value) {
    return typeof value === 'object' && value !== null;
}

function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function resolveCssLengthVariable(variableName, fallbackValue) {
    if (!(document.documentElement instanceof HTMLElement)) {
        return fallbackValue;
    }

    const resolvedValue = window.getComputedStyle(document.documentElement)
        .getPropertyValue(variableName)
        .trim();

    return resolvedValue === '' ? fallbackValue : resolvedValue;
}
